import React, { useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { ChatProviders } from './features/chat/context/ChatProviders';
import { ToastContainer } from 'react-toastify';
import MainLayout from './components/layouts/MainLayout';
import HomeLayout from './components/layouts/HomeLayout';
import 'react-toastify/dist/ReactToastify.css';
import './index.css';
import ProtectedRoute from './ProtectedRoute';
import WaitlistGuard from './components/WaitlistGuard';
import RootRedirect from './components/RootRedirect';
import WaitlistIntegrationTest from './components/WaitlistIntegrationTest';
import { ShareModal } from './features/share';
import ReportModal from './components/ReportModal';
import DeleteConfirmModal from './components/DeleteConfirmModal';
import ScrollManager from './components/ScrollManager';
import { useSelector, useDispatch } from 'react-redux';
import { setupPostsListener, cleanupPostsListener, hydratePostsFromCache, setupUserLikesListener, cleanupUserLikesListener } from './redux/slices/postSlice';
import { hydrateTrendingFromCache, setupTrendingListener, cleanupTrendingListener } from './redux/slices/trendingSlice';
import { hydrateFeedProjectsFromCache, setupFeedProjectsListener, cleanupFeedProjectsListener } from './redux/slices/feedProjectsSlice';
import { fetchSeenHistory } from './features/feed';
import { useQueryClient } from '@tanstack/react-query';
import { prefetchProfileTab } from './hooks/queries/profileQueries';
import { setCurrentPath, closeSidebarOnMobile } from './redux/slices/uiSlice';
import { setupCommentLikesListener, setupReplyLikesListener, cleanupCommentLikesListener, cleanupReplyLikesListener } from './redux/slices/posts/commentSlice';

import ErrorBoundary from './components/ErrorBoundary.js';
import { initialize, cleanup } from './utils/performanceOptimizations';
import firestoreConnectionManager from './utils/firestoreConnectionManager.js';

// Lazy load heavy components to improve startup performance
const Feed = lazy(() => import(/* webpackPrefetch: true */ './pages/home/Feed'));
// Deactivated old UserProfile route; keep directory for reference only
const MyAccount = lazy(() => import(/* webpackPrefetch: true */ './pages/home/MyAccount/MyAccount'));
const ProfilePage = lazy(() => import(/* webpackPrefetch: true */ './pages/home/ProfilePage'));
const Files = lazy(() => import(/* webpackPrefetch: true */ './pages/home/Files'));
const SearchResults = lazy(() => import(/* webpackPrefetch: true */ './pages/home/SearchResults'));
const Notifications = lazy(() => import(/* webpackPrefetch: true */ './pages/Notifications'));
const LoginPage = lazy(() => import('./pages/auth/LoginPage.jsx'));
const SignUpPage = lazy(() => import('./pages/auth/SignUpPage.jsx'));
const ForgotPassword = lazy(() => import('./pages/auth/ForgotPassword.jsx'));
const VerifyEmail = lazy(() => import('./pages/auth/VerifyEmail.jsx'));
const AuthLayout = lazy(() => import('./pages/auth/AuthLayout.jsx'));
const CountryMapsGallery = lazy(() => import('./pages/dev/CountryMapsGallery'));
const SolutionBuilderPageEnhanced = lazy(() => import(/* webpackPrefetch: true */ './features/solutions/SolutionBuilderPage'));
const SolutionView = lazy(() => import(/* webpackPrefetch: true */ './features/solutions/SolutionView'));
const DiscoverProblemsPage = lazy(() => import('./pages/home/DiscoverProblems/DiscoverProblemsPage.jsx'));
// INACTIVE PATHS - Deactivated for now, focusing on FromPostbox only
// const NewProjectPage = lazy(() => import('./pages/home/Projects/inactive-paths/FromSolution/NewProjectPage.jsx'));
// const SolutionProjectConstructPage = lazy(() => import('./pages/home/Projects/inactive-paths/FromSolution/ProjectConstructPage.jsx'));
// const BrowseSolutionsPage = lazy(() => import('./pages/home/Projects/inactive-paths/FromSolution/BrowseSolutionsPage.jsx'));
// const CreatePostFromSolution = lazy(() => import('./pages/home/Projects/inactive-paths/Shared/CreatePostFromSolution.jsx'));
// const CreateTeam = lazy(() => import('./pages/home/Projects/inactive-paths/Shared/CreateTeam.jsx'));
// const PostProjectPage = lazy(() => import('./pages/home/Projects/inactive-paths/FromPost/PostProjectPage.jsx'));
// const PostProjectConstructPage = lazy(() => import('./pages/home/Projects/inactive-paths/FromPost/ProjectConstructPage.jsx'));

// ACTIVE PATH - FromPostbox only
const CreateProjectDetails = lazy(() => import('./pages/home/Projects/FromPostbox/CreateProjectDetails.jsx'));
const CreateTeam = lazy(() => import('./pages/home/Projects/FromPostbox/CreateTeam.jsx'));
const ProjectIntroPage = lazy(() => import('./pages/home/Projects/FromPostbox/ProjectIntroPage.jsx'));
const ProjectBuildPage = lazy(() => import('./pages/home/Projects/FromPostbox/ProjectBuildPage'));
const ProjectBuildPageFromPost = lazy(() => import('./pages/home/Projects/FromPost/ProjectBuildPage.jsx'));
const ProjectBuildPageFromSolution = lazy(() => import('./pages/home/Projects/FromSolution/ProjectBuildPage.jsx'));

// NEW ACTIVE PATHS - FromPost and FromSolution
const PostProjectIntro = lazy(() => import('./pages/home/Projects/FromPost/PostProjectIntro.jsx'));
const BrowseSolutionsForPost = lazy(() => import('./pages/home/Projects/FromPost/BrowseSolutionsForPost.jsx'));
const CreateTeamFromPost = lazy(() => import('./pages/home/Projects/FromPost/CreateTeam.jsx'));
const SolutionProjectIntro = lazy(() => import('./pages/home/Projects/FromSolution/SolutionProjectIntro.jsx'));
const SolutionPostReview = lazy(() => import('./pages/home/Projects/FromSolution/SolutionPostReview.jsx'));
const CreateTeamFromSolution = lazy(() => import('./pages/home/Projects/FromSolution/CreateTeam.jsx'));
const YourProjectsPage = lazy(() => import('./pages/home/Projects/YourProjects/index.jsx'));
const FollowedProjectsPage = lazy(() => import('./pages/home/Projects/Pages/FollowedProjectsPage.jsx'));
const ProjectDetailsPage = lazy(() => import('./pages/home/Projects/ProjectDetails/ProjectDetailsPage.js'));
const PostPage = lazy(() => import('./pages/PostPage.jsx'));

// Component showcase for capturing screenshots
// const ComponentShowcase = lazy(() => import('./pages/ComponentShowcase.jsx'));

// Waitlist components
const WaitlistPage = lazy(() => import('./pages/waitlist/WaitlistPage.jsx'));
const WaitlistConfirmationPage = lazy(() => import('./pages/waitlist/WaitlistConfirmationPage.jsx'));
const WaitlistAdminPage = lazy(() => import('./pages/admin/WaitlistAdminPage.jsx'));
const AdminReportsPage = lazy(() => import('./pages/admin/AdminReportsPage.jsx'));

// Route change tracker component
const RouteChangeTracker = () => {
  const dispatch = useDispatch();
  const location = useLocation();

  useEffect(() => {
    dispatch(setCurrentPath(location.pathname));
    dispatch(closeSidebarOnMobile());
  }, [location, dispatch]);

  return null;
};

// Removed unused ErrorFallback component

function App() {
  const isLoading = useSelector(state => state.ui.isLoading);
  const isAuthenticated = useSelector(state => state.auth.isAuthenticated);
  const currentUserId = useSelector(state => state.auth?.currentUser?.uid);
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  // Feature flag: force public auth routes to waitlist when enabled
  const WAITLIST_ENABLED = process.env.REACT_APP_WAITLIST_ENABLED === 'true';

  // Initialize performance optimizations
  useEffect(() => {
    initialize(dispatch);
    // Initialize Firestore connection manager
    // Start posts listener once at app root
    // Hydrate cached posts immediately for instant first paint
    dispatch(hydratePostsFromCache());
    dispatch(setupPostsListener());
    // Hydrate and start trending listeners once at app root
    dispatch(hydrateTrendingFromCache());
    dispatch(setupTrendingListener());
    // Hydrate and start feed projects listener once at app root
    dispatch(hydrateFeedProjectsFromCache());
    dispatch(setupFeedProjectsListener());

    return () => {
      cleanup();
      firestoreConnectionManager.cleanup();
      dispatch(cleanupPostsListener());
      dispatch(cleanupTrendingListener());
      dispatch(cleanupFeedProjectsListener());
    };
  }, [dispatch]);

  // Dev-only logging for key app state
  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') {
      console.log('[App] state', { isLoading, isAuthenticated });
    }
  }, [isLoading, isAuthenticated]);

  // Prefetch Profile tabs for current user to avoid reloads when navigating
  useEffect(() => {
    if (!currentUserId || !queryClient) return;
    const tabs = ['posts', 'solutions', 'comments', 'projects'];
    tabs.forEach(t => prefetchProfileTab(queryClient, t, currentUserId));
  }, [currentUserId, queryClient]);

  // Start/cleanup a user-scoped likes listener to hydrate post isLiked state globally
  useEffect(() => {
    if (!currentUserId) {
      dispatch(cleanupUserLikesListener());
      dispatch(cleanupCommentLikesListener());
      dispatch(cleanupReplyLikesListener());
      return;
    }
    dispatch(setupUserLikesListener(currentUserId));
    dispatch(setupCommentLikesListener(currentUserId));
    dispatch(setupReplyLikesListener(currentUserId));
    // Hydrate seen history from Firestore for this user
    dispatch(fetchSeenHistory(currentUserId));
    return () => {
      dispatch(cleanupUserLikesListener());
      dispatch(cleanupCommentLikesListener());
      dispatch(cleanupReplyLikesListener());
    };
  }, [currentUserId, dispatch]);

  // Create a portal container for toasts if it doesn't exist
  useEffect(() => {
    let toastPortal = document.getElementById('toast-portal-container');

    if (!toastPortal) {
      toastPortal = document.createElement('div');
      toastPortal.id = 'toast-portal-container';
      toastPortal.style.position = 'fixed';
      toastPortal.style.top = '0';
      toastPortal.style.left = '0';
      toastPortal.style.width = '100%';
      document.body.appendChild(toastPortal);
    }
  }, []);

  // Loading fallback component
  const LoadingFallback = () => (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '50vh',
      fontSize: '16px',
      color: '#666'
    }}>
      Loading...
    </div>
  );

  // Lightweight route-specific skeletons
  const FeedSkeleton = () => (
    <div style={{ padding: 16 }}>
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={i} style={{ background: '#f3f4f6', border: '1px solid #e5e7eb', borderRadius: 8, padding: 16, marginBottom: 12 }}>
          <div style={{ height: 16, width: '40%', background: '#e5e7eb', borderRadius: 4, marginBottom: 8 }} />
          <div style={{ height: 12, width: '85%', background: '#e5e7eb', borderRadius: 4, marginBottom: 6 }} />
          <div style={{ height: 12, width: '70%', background: '#e5e7eb', borderRadius: 4 }} />
        </div>
      ))}
    </div>
  );

  const FilesSkeleton = () => (
    <div style={{ padding: 16 }}>
      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        {['Posts', 'Comments', 'Solutions'].map((t) => (
          <div key={t} style={{ height: 28, width: 90, background: '#e5e7eb', borderRadius: 9999 }} />
        ))}
      </div>
      {Array.from({ length: 2 }).map((_, i) => (
        <div key={i} style={{ background: '#f3f4f6', border: '1px solid #e5e7eb', borderRadius: 8, padding: 16, marginBottom: 12 }}>
          <div style={{ height: 14, width: '60%', background: '#e5e7eb', borderRadius: 4, marginBottom: 6 }} />
          <div style={{ height: 12, width: '80%', background: '#e5e7eb', borderRadius: 4 }} />
        </div>
      ))}
    </div>
  );

  const ProfileSkeleton = () => (
    <div style={{ padding: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <div style={{ width: 64, height: 64, borderRadius: '50%', background: '#e5e7eb' }} />
        <div>
          <div style={{ height: 16, width: 160, background: '#e5e7eb', borderRadius: 4, marginBottom: 6 }} />
          <div style={{ height: 12, width: 220, background: '#e5e7eb', borderRadius: 4 }} />
        </div>
      </div>
      <div style={{ height: 12, width: '90%', background: '#e5e7eb', borderRadius: 4, marginBottom: 6 }} />
      <div style={{ height: 12, width: '75%', background: '#e5e7eb', borderRadius: 4 }} />
    </div>
  );

  const SolutionViewSkeleton = () => (
    <div style={{ padding: 16 }}>
      <div style={{ height: 18, width: '60%', background: '#e5e7eb', borderRadius: 4, marginBottom: 12 }} />
      <div style={{ height: 12, width: '80%', background: '#e5e7eb', borderRadius: 4, marginBottom: 6 }} />
      <div style={{ height: 12, width: '70%', background: '#e5e7eb', borderRadius: 4 }} />
    </div>
  );

  return (
    <ErrorBoundary>
      <AuthProvider>
        <ToastContainer
          position="top-center"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="light"
          bodyClassName="global-toast-body"
          containerId="global-toast-container"
          enableMultiContainer
          role="status"
          aria-live="polite"
        />

        <Router>
          <ChatProviders>
            {/* Track route changes */}
            <RouteChangeTracker />

            {/* Use ScrollManager to handle scroll behavior */}
            <ScrollManager />

            <Suspense fallback={<LoadingFallback />}>
              <Routes>
                {/* Root path redirects based on auth state and waitlist */}
                <Route path="/" element={<RootRedirect />} />

                {/* Waitlist Routes - Publicly accessible (standalone, not using AuthLayout) */}
                <Route path="/waitlist" element={<WaitlistPage />} />
                <Route path="/waitlist/confirmation" element={<WaitlistConfirmationPage />} />

                {/* Auth Routes - Publicly accessible */}
                <Route element={<AuthLayout />}>
                  {WAITLIST_ENABLED ? (
                    <>
                      {/* Allow login for existing users even when waitlist is enabled */}
                      <Route path="/login" element={<LoginPage />} />
                      {/* Admin direct access route remains */}
                      <Route path="/admin/login" element={<LoginPage />} />
                      {/* Show real signup page; approval will be enforced in SignUpPage before account creation */}
                      <Route path="/signup" element={<SignUpPage />} />
                      <Route path="/forgot-password" element={<ForgotPassword />} />
                      <Route path="/verify-email" element={<VerifyEmail />} />
                    </>
                  ) : (
                    <>
                      <Route path="/login" element={<LoginPage />} />
                      <Route path="/signup" element={<SignUpPage />} />
                      <Route path="/forgot-password" element={<ForgotPassword />} />
                      <Route path="/verify-email" element={<VerifyEmail />} />
                    </>
                  )}
                </Route>

                {/* Protected Routes */}
                <Route path="/home" element={
                  <WaitlistGuard>
                    <ProtectedRoute>
                      <HomeLayout />
                    </ProtectedRoute>
                  </WaitlistGuard>
                }>
                  <Route index element={<Suspense fallback={null}><Feed /></Suspense>} />
                </Route>

                {/* Other Protected Routes with MainLayout */}
                <Route element={
                  <WaitlistGuard>
                    <ProtectedRoute>
                      <MainLayout />
                    </ProtectedRoute>
                  </WaitlistGuard>
                }>
                  <Route path="/profile" element={<Suspense fallback={<ProfileSkeleton />}><MyAccount /></Suspense>} />
                  <Route path="/profile/:userId" element={<Suspense fallback={<ProfileSkeleton />}><ProfilePage /></Suspense>} />
                  <Route path="/files" element={<Suspense fallback={<FilesSkeleton />}><Files /></Suspense>} />
                  <Route path="/search" element={<Suspense fallback={<FeedSkeleton />}><SearchResults /></Suspense>} />
                  <Route path="/notifications" element={<Suspense fallback={<FeedSkeleton />}><Notifications /></Suspense>} />
                  {/* INACTIVE ROUTES - Deactivated for now, focusing on FromPostbox only */}
                  {/* <Route path="/projects/new" element={<NewProjectPage />} /> */}
                  {/* <Route path="/projects/construct" element={<SolutionProjectConstructPage />} /> */}
                  {/* <Route path="/projects/post/new" element={<PostProjectPage />} /> */}
                  {/* <Route path="/projects/post/construct" element={<PostProjectConstructPage />} /> */}
                  {/* <Route path="/solutions/browse" element={<BrowseSolutionsPage />} /> */}
                  {/* <Route path="/projects/create-post" element={<CreatePostFromSolution />} /> */}
                  {/* <Route path="/projects/create-team" element={<CreateTeam />} /> */}

                  {/* ACTIVE ROUTES - FromPostbox only */}
                  <Route path="/projects/postbox/new" element={<ProjectIntroPage />} />
                  <Route path="/projects/postbox/post" element={<CreateProjectDetails />} />
                  <Route path="/projects/postbox/team" element={<CreateTeam />} />
                  <Route path="/projects/postbox/build" element={<ProjectBuildPage />} />

                  {/* NEW ACTIVE ROUTES - FromPost Path */}
                  <Route path="/projects/from-post/intro" element={<PostProjectIntro />} />
                  <Route path="/projects/from-post/browse" element={<BrowseSolutionsForPost />} />
                  <Route path="/projects/from-post/team" element={<CreateTeamFromPost />} />
                  <Route path="/projects/from-post/build" element={<ProjectBuildPageFromPost />} />

                  {/* NEW ACTIVE ROUTES - FromSolution Path */}
                  <Route path="/projects/from-solution/intro" element={<SolutionProjectIntro />} />
                  <Route path="/projects/from-solution/review" element={<SolutionPostReview />} />
                  <Route path="/projects/from-solution/team" element={<CreateTeamFromSolution />} />
                  <Route path="/projects/from-solution/build" element={<ProjectBuildPageFromSolution />} />
                  <Route path="/solutions/:postId/build" element={<SolutionBuilderPageEnhanced />} />
                  <Route path="/solutions/:solutionId" element={<Suspense fallback={<SolutionViewSkeleton />}><SolutionView /></Suspense>} />
                  <Route path="/discover/problems" element={<DiscoverProblemsPage />} />
                  <Route path="/projects/:projectId" element={<Suspense fallback={<FeedSkeleton />}><ProjectDetailsPage /></Suspense>} />
                  <Route path="/contributions" element={<div className="placeholder-page"><h2>Contributions</h2><p>This page is under construction</p></div>} />
                  <Route path="/followed-projects" element={<FollowedProjectsPage />} />
                  <Route path="/your-projects/*" element={<YourProjectsPage />} />
                  <Route path="/admin/waitlist" element={<WaitlistAdminPage />} />
                  <Route path="/admin/reports" element={<AdminReportsPage />} />
                </Route>

                {/* Dev route: Country SVG gallery */}
                <Route path="/dev/country-maps" element={<CountryMapsGallery />} />

                {/* Post permalink route - redirects to /home?post={postId} */}
                <Route path="/post/:postId" element={<PostPage />} />

                {/* Fallback */}
                <Route path="*" element={<Navigate to="/home" replace />} />
              </Routes>
            </Suspense>
            {/* Global Modal Components */}
            <ShareModal />
            <ReportModal />
            <DeleteConfirmModal />
          </ChatProviders>
        </Router>

        {/* Development Integration Test */}
        <WaitlistIntegrationTest />

        {/* Global Loading Indicator */}
        {isLoading && (
          <div className="global-loading-indicator">
            <div className="spinner"></div>
          </div>
        )}
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
